import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import EditPencilIcon from "@redotech/redo-icons/edit-01.svg";
import { StorefrontClientContext } from "@redotech/redo-merchant-app-common/team";
import {
  rcoProductCollectionsSegmentSchema,
  RcoProductCollectionsSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { SegmentListMultipleItemMembershipOperator } from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { OperatorSelector } from "@redotech/redo-web/conversion/operator-selector";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { getCollectionsByGids } from "@redotech/shopify-storefront";
import {
  convertShopifyIdToGid,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { PartialWithRequiredType } from "../..";
import { CollectionSelectionModal } from "./collection-selection-modal";

export const ProductCollectionsInspectorContent = memo(
  function ProductCollectionsInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const storefrontClient = useRequiredContext(StorefrontClientContext);
    const segment = useMemo(
      () => rcoProductCollectionsSegmentSchema.parse(step.segment),
      [step],
    );

    const collectionNamesLoad = useLoad(async () => {
      if (segment.collectionIds.length === 0) {
        return {};
      }

      const gids = segment.collectionIds.map((id) =>
        convertShopifyIdToGid(id, ShopifyIdType.Collection),
      );
      const collections = await getCollectionsByGids(storefrontClient, gids);
      const nameMap: { [id: string]: string } = {};
      for (const collection of collections) {
        nameMap[collection.id] = collection.title;
      }
      return nameMap;
    }, [segment.collectionIds, storefrontClient]);

    if (segment.collectionIds.length > 0 && !collectionNamesLoad.value) {
      return (
        <Flex align="center" dir="row" justify="center" p="md" w="full">
          <LoadingRedoAnimation />
        </Flex>
      );
    }

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {operatorsToText(
              segment.cartOperator,
              segment.collectionListOperator,
            )}
          </Text>
        </Flex>
        <Flex dir="row" gap="md" wrap="wrap">
          {segment.collectionIds.length === 0 ? (
            <Text fontSize="xs" textColor="tertiary">
              No collections selected
            </Text>
          ) : (
            segment.collectionIds.map((collectionId, index) => {
              const collectionName =
                collectionNamesLoad.value?.[collectionId] || collectionId;
              return (
                <RedoBadge
                  color="gray"
                  key={index}
                  size="sm"
                  text={collectionName}
                />
              );
            })
          )}
        </Flex>
      </>
    );
  },
);

const operatorsToText = (
  cartOperator: RcoProductCollectionsSegmentSchema["cartOperator"],
  collectionListOperator: RcoProductCollectionsSegmentSchema["collectionListOperator"],
) => {
  switch (cartOperator) {
    case SegmentListMultipleItemMembershipOperator.ANY_IN:
      switch (collectionListOperator) {
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return "Any product in the cart belongs to any of the collections in the list:";
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return "Any product in the cart belongs to all of the collections in the list:";
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return "Any product in the cart does not belong to any of the collections in the list:";
        default:
          assertNever(collectionListOperator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.ALL_IN:
      switch (collectionListOperator) {
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return "All products in the cart belong to any of the collections in the list:";
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return "All products in the cart belong to all of the collections in the list:";
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return "All products in the cart do not belong to any of the collections in the list:";
        default:
          assertNever(collectionListOperator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.NONE_IN:
      switch (collectionListOperator) {
        case SegmentListMultipleItemMembershipOperator.ANY_IN:
          return "No products in the cart belong to any of the collections in the list:";
        case SegmentListMultipleItemMembershipOperator.ALL_IN:
          return "No products in the cart belong to all of the collections in the list:";
        case SegmentListMultipleItemMembershipOperator.NONE_IN:
          return "No products in the cart do not belong to any of the collections in the list:";
        default:
          assertNever(collectionListOperator);
      }
      break;
    default:
      assertNever(cartOperator);
  }
};

export const segmentListMultipleItemMembershipOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "Any",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "No",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "Every",
};

export const collectionListMultipleItemMembershipOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "any of",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "all of",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "none of",
};

export const EditableProductCollectionsInspectorContent = memo(
  function EditableProductCollectionsInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const storefrontClient = useRequiredContext(StorefrontClientContext);
    const [modalOpen, setModalOpen] = useState(false);
    const segment = useMemo(
      () => rcoProductCollectionsSegmentSchema.parse(step.segment),
      [step],
    );

    const collectionNamesLoad = useLoad(async () => {
      if (segment.collectionIds.length === 0) {
        return {};
      }

      const gids = segment.collectionIds.map((id) =>
        convertShopifyIdToGid(id, ShopifyIdType.Collection),
      );
      const collections = await getCollectionsByGids(storefrontClient, gids);
      const nameMap: { [id: string]: string } = {};
      for (const collection of collections) {
        nameMap[collection.id] = collection.title;
      }
      return nameMap;
    }, [segment.collectionIds, storefrontClient]);

    const handleCartOperatorSelected = useHandler(
      (operator: SegmentListMultipleItemMembershipOperator) => {
        setStep({ ...step, segment: { ...segment, cartOperator: operator } });
      },
    );

    const handleCollectionListOperatorSelected = useHandler(
      (operator: SegmentListMultipleItemMembershipOperator) => {
        setStep({
          ...step,
          segment: { ...segment, collectionListOperator: operator },
        });
      },
    );

    const handleCollectionsSelected = useHandler((collectionIds: string[]) => {
      const updatedSegment = { ...segment, collectionIds };
      setStep({ ...step, segment: updatedSegment });
      setModalOpen(false);
    });

    if (segment.collectionIds.length > 0 && !collectionNamesLoad.value) {
      return (
        <Flex align="center" dir="row" justify="center" p="md" w="full">
          <LoadingRedoAnimation />
        </Flex>
      );
    }

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Flex>
            <OperatorSelector<SegmentListMultipleItemMembershipOperator>
              availableOptions={Object.values(
                SegmentListMultipleItemMembershipOperator,
              )}
              onOperatorSelected={handleCartOperatorSelected}
              operatorToText={segmentListMultipleItemMembershipOperatorToText}
              selectedOperator={segment.cartOperator}
            />
          </Flex>
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          product in the cart belongs to
        </Text>
        <Flex>
          <OperatorSelector<SegmentListMultipleItemMembershipOperator>
            availableOptions={Object.values(
              SegmentListMultipleItemMembershipOperator,
            )}
            onOperatorSelected={handleCollectionListOperatorSelected}
            operatorToText={collectionListMultipleItemMembershipOperatorToText}
            selectedOperator={segment.collectionListOperator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          the collections in this list:
        </Text>
        <Flex dir="row" gap="md" w="full" wrap="wrap">
          {segment.collectionIds.length === 0 ? (
            <Text fontSize="xs" textColor="tertiary">
              No collections selected
            </Text>
          ) : (
            segment.collectionIds.map((collectionId, index) => {
              const collectionName =
                collectionNamesLoad.value?.[collectionId] || collectionId;
              return (
                <RedoBadge
                  color="gray"
                  key={index}
                  size="sm"
                  text={collectionName}
                />
              );
            })
          )}
        </Flex>
        <Flex justify="flex-end" w="full">
          <RedoButton
            hierarchy="secondary"
            IconLeading={EditPencilIcon}
            onClick={() => setModalOpen(true)}
            size="xs"
            text="Edit list"
          />
        </Flex>
        {modalOpen && (
          <CollectionSelectionModal
            onClose={() => setModalOpen(false)}
            onCollectionsSelected={handleCollectionsSelected}
            selectedCollections={segment.collectionIds}
          />
        )}
      </>
    );
  },
);
